import { Market } from '@staycool/location';
import { getServiceUrl, httpGet, httpPost } from '../services/api';
import {
    ArticleResponse,
    Avatar,
    CmsBannerMessage,
    PostCountFromTimestamp,
    PostCountsByCategory,
    TranslationUpdate,
} from '../services/cms/types';
import { Language } from '../services/language/types';
import { CmsGeneralSetting } from '../services/sports/types';
import { getStoreValue } from '../stores/store/utils';
import { stores } from '../stores';
import { logger } from '../services/logger';
import { BlogPost } from '../services/cms-blog';

const getUrl = (url) => getServiceUrl('cms', url);

export async function getBannerMessages(market: Market) {
    const url = getUrl('banner-message/user');
    return httpGet<CmsBannerMessage[]>(url, { market });
}

export async function getFoTranslations(language: string, fallbackLanguage: string) {
    const url = getUrl(`translations/fo/${language}`);
    return httpGet<{ translationsByKey: Record<string, string>; lastUpdated: string }>(url, {
        fallbackLanguage,
        version: process.env.REACT_APP_VERSION || 'development',
    });
}

export function getIncrementalTranslationUpdates(
    language: Language,
    fallbackLanguage: Language,
    lastUpdatedUnixTime: string,
) {
    const url = getUrl(`translations/fo/incremental/${language}/${lastUpdatedUnixTime}`);
    return httpGet<TranslationUpdate>(url, {
        fallbackLanguage,
        version: process.env.REACT_APP_VERSION || 'development',
    });
}

export function reportMissingTranslations(missingTranslationReports) {
    const url = getUrl('translations/missing/report');
    return httpPost(url, missingTranslationReports);
}

export async function getCmsSettings() {
    const url = getUrl('settings');
    return await httpGet<CmsGeneralSetting[]>(url);
}

export async function getArticleBySlug(pageSlug: string, language: Language) {
    const url = getUrl(`articles/public/by-slug/${pageSlug}/${language}`);
    return httpGet<ArticleResponse>(url);
}

export async function getArticleForPreviewBySlug(pageSlug: string, language: Language) {
    const url = getUrl(`articles/preview/${pageSlug}/${language}`);
    return httpGet<ArticleResponse>(url);
}

export function getArticleById(id: string | number) {
    const url = getUrl(`articles/public/${id}`);
    return httpGet<ArticleResponse>(url);
}

export function checkIsUserEligibleForContentBySegment(segmentId: string) {
    const url = getUrl(`articles/user-segment-check/${segmentId}`);
    return httpGet<{ isUserInSegment: boolean }>(url);
}

export async function getAvatars() {
    const url = getUrl('avatars');
    return httpGet<Avatar[]>(url);
}

export async function loadBlogCategories(market: string) {
    const language = getStoreValue(stores.language);
    const categories = getStoreValue(stores.blog.categories);

    if (categories.length) {
        return;
    }

    const url = getUrl(`posts/post_counts_by_category/Coolbet/${market}/${language}`);

    try {
        const response = await httpGet<PostCountsByCategory[]>(url);
        stores.blog.categories.set(response);
    } catch (error) {
        logger.error('CmsMicroservice', 'loadBlogCategories', error);
    }
}

export async function getNewPostsAmount() {
    const language = getStoreValue(stores.language);
    const fromDate = getStoreValue(stores.blog.blogVisitDate);
    const url = getUrl(`posts/count_from_timestamp/${language}?timestamp=${fromDate}`);
    try {
        const response = await httpGet<PostCountFromTimestamp>(url);
        stores.blog.newBlogPostsCount.set(response.count);
    } catch (error) {
        logger.error('CmsMicroservice', 'getNewPostsAmount', error);
    }
}

export async function getBlogArticle(blogSlug) {
    const language = getStoreValue(stores.language);
    const url = getUrl(`posts/by_slug/Coolbet/${language}/${blogSlug}`);
    return httpGet<BlogPost>(url);
}

export async function loadArticlesByParams({
    category,
    limit,
    offset,
    excludeArticleId,
    market,
}: {
    category: string;
    limit: number;
    offset?: number;
    excludeArticleId?: string;
    market?: Market;
}) {
    const language = getStoreValue(stores.language);
    let articles = [] as BlogPost[];

    try {
        const queryParameters = { category, limit, offset, excludeArticleId, market };
        const url = getUrl(`posts/by_params/${language}`);
        articles = await httpGet<BlogPost[]>(url, queryParameters);
    } catch (error) {
        logger.error('CmsMicroservice', 'loadArticlesByParams', error);
    }

    return articles;
}

export async function getBlogPreview(draftId) {
    const language = getStoreValue(stores.language);
    const url = getUrl(`drafts/${language}/${draftId}/`);
    return httpGet<any>(url);
}

export async function getSnippet(key: string, overrideLanguage?: Language) {
    const language = overrideLanguage || getStoreValue(stores.language);
    const url = getUrl(`snippets/by_lang/${language}/${key}`);
    const response = await httpGet<{ body: any }>(url);
    return response.body;
}
